<template>
    <div class="video-feed">
        <div v-if="currentVideo" class="video-feed-inner">
            <transition name="fade" mode="out-in">
                <div :key="currentVideo.creatorVideo.id">
                    <div class="video-stage position-relative">
                        <div
                            class="overlay-avatar"
                            @click="viewProfile(currentVideo.standardMap)"
                        >
                            <img
                                v-if="currentVideo.standardMap.primaryPhoto"
                                :src="currentVideo.standardMap.primaryPhoto.small"
                                class="avatar-img"
                            />
                            <user-online-status
                                size="small"
                                :standardMap="currentVideo.standardMap"
                                class="avatar-online"
                            />
                            <img
                                v-if="currentVideo.standardMap.flagUrl"
                                :src="currentVideo.standardMap.flagUrl"
                                class="avatar-flag"
                            />
                        </div>

                        <b-dropdown
                            right
                            no-caret
                            variant="link"
                            class="overlay-menu"
                        >
                            <template #button-content>
                                <span class="menu-btn">
                                    <font-awesome-icon
                                        icon="fa-solid fa-bars"
                                    />
                                </span>
                            </template>
                            <creator-video-share-dialog />
                            <b-dropdown-item
                                :href="currentVideo.creatorVideo.mp4Url"
                                target="_blank"
                            >
                                <font-awesome-icon
                                    icon="fa-light fa-arrow-up-right-from-square"
                                    class="mr-1"
                                />
                                View Original
                            </b-dropdown-item>
                            <b-dropdown-divider />
                            <b-dropdown-text>
                                <div class="small text-secondary">
                                    <font-awesome-icon
                                        icon="fa-light fa-calendar"
                                        class="mr-1"
                                    />
                                    Posted {{ currentVideo.creatorVideo.publishDateTerse }}
                                </div>
                            </b-dropdown-text>
                            <b-dropdown-divider />
                            <b-dropdown-item
                                @click="reportModal = true"
                                link-class="text-danger"
                            >
                                <font-awesome-icon
                                    icon="fa-solid fa-flag"
                                    class="mr-1"
                                />
                                Report
                            </b-dropdown-item>
                        </b-dropdown>
                        <creator-video-play
                            :creatorVideo="currentVideo.creatorVideo"
                        />

                        <div class="playback-controls">
                            <button
                                @click="togglePlay"
                                class="control-btn"
                                type="button"
                            >
                                <font-awesome-icon
                                    :icon="
                                        isPlaying
                                            ? 'fa-solid fa-pause'
                                            : 'fa-solid fa-play'
                                    "
                                />
                            </button>
                            <button
                                @click="toggleMute"
                                class="control-btn"
                                type="button"
                            >
                                <font-awesome-icon
                                    :icon="
                                        isMuted
                                            ? 'fa-solid fa-volume-slash'
                                            : 'fa-solid fa-volume'
                                    "
                                />
                            </button>
                        </div>

                        <button
                            v-if="isCreatorOnline"
                            @click="callCreator"
                            class="call-now-btn"
                            type="button"
                        >
                            <span class="call-now-online">
                                <span class="online-dot" />
                                Online Now
                            </span>
                            <span class="call-now-action">
                                <font-awesome-icon
                                    icon="fa-solid fa-phone"
                                />
                                Call
                            </span>
                        </button>
                        <button
                            v-else
                            @click="chatCreator"
                            class="chat-btn"
                            :class="{ 'chat-btn-unread': hasUnreadFromCreator }"
                            type="button"
                        >
                            <font-awesome-icon
                                icon="fa-solid fa-comment"
                            />
                            <span
                                v-if="hasUnreadFromCreator"
                                class="unread-dot"
                            />
                        </button>

                        <div class="progress-track">
                            <div
                                class="progress-fill"
                                :style="{ width: progress + '%' }"
                            />
                        </div>

                        <transition name="reaction-pop">
                            <div
                                v-if="reactionIcon"
                                :key="reactionKey"
                                class="reaction-overlay"
                            >
                                <font-awesome-icon
                                    :icon="reactionIcon"
                                    :class="reactionClass"
                                />
                            </div>
                        </transition>
                    </div>

                    <div class="rate-row">
                        <button
                            @click="rate(0)"
                            class="rate-btn rate-down"
                            :disabled="rating"
                            type="button"
                        >
                            <font-awesome-icon
                                icon="fa-solid fa-thumbs-down"
                                size="2x"
                            />
                        </button>
                        <button
                            @click="rate(1)"
                            class="rate-btn rate-up"
                            :disabled="rating"
                            type="button"
                        >
                            <font-awesome-icon
                                icon="fa-solid fa-thumbs-up"
                                size="2x"
                            />
                        </button>
                    </div>
                </div>
            </transition>
        </div>
        <div v-else-if="reachedTheEnd" class="text-center p-3">
            <h3>The End</h3>
            <p>
                You've reached the end. Check back later for new videos.
            </p>
            <b-btn variant="primary" @click="startOver" pill>
                Start Over
            </b-btn>
        </div>
        <div v-else>
            <generic-loading />
        </div>

        <b-modal
            v-model="reportModal"
            centered
            hide-footer
            title="Report Video"
        >
            <div v-if="!reported" class="text-center p-3">
                <font-awesome-icon
                    icon="fa-solid fa-flag"
                    class="text-danger mb-3"
                    size="3x"
                />
                <h6>Are you sure you want to report this video?</h6>
                <p class="small text-secondary">
                    This will flag the video for review by our team.
                </p>
                <div class="d-flex gap-2 mt-3">
                    <b-btn
                        @click="reportModal = false"
                        variant="light"
                        pill
                        block
                    >
                        Cancel
                    </b-btn>
                    <b-btn
                        @click="reportVideo"
                        variant="danger"
                        pill
                        block
                    >
                        Report
                    </b-btn>
                </div>
            </div>
            <div v-else class="text-center p-3">
                <font-awesome-icon
                    icon="fa-light fa-check-circle"
                    class="text-success mb-3"
                    size="3x"
                />
                <h6>Video Reported</h6>
                <p class="small text-secondary">
                    Thanks for letting us know. We'll review this video.
                </p>
                <b-btn
                    @click="reportModal = false; reported = false"
                    variant="primary"
                    pill
                    block
                    class="mt-3"
                >
                    OK
                </b-btn>
            </div>
        </b-modal>
    </div>
</template>

<style lang="scss" scoped>
@import "@/scss/variables.scss";

.video-feed {
    padding: 12px;
    padding-bottom: 0;
}

.video-feed-inner {
    max-width: 450px;
    margin: 0 auto;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.video-stage {
    overflow: hidden;
    background: #000;
    border-radius: 6px;

    // fixed height: nav headers (108px) + rate buttons (86px)
    //              + padding (22px) = 216px
    height: calc((var(--vh, 1vh) * 100) - 216px);
    display: flex;
    align-items: center;
    justify-content: center;

    ::v-deep video {
        max-height: calc((var(--vh, 1vh) * 100) - 216px);
        width: 100%;
        object-fit: contain;
    }
}

.overlay-avatar {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 4;
    cursor: pointer;
    width: 44px;
    height: 44px;
}

.avatar-img {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid rgba(255, 255, 255, 0.4);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
}

.avatar-online {
    position: absolute;
    bottom: -1px;
    left: -1px;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
}

.avatar-flag {
    position: absolute;
    bottom: -2px;
    right: -4px;
    width: 16px;
    height: 12px;
    border-radius: 2px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
}

.overlay-menu {
    position: absolute;
    top: 8px;
    right: 4px;
    z-index: 4;
}

.menu-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.25);
    color: #fff;
    font-size: 0.95rem;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
}

.playback-controls {
    position: absolute;
    bottom: 14px;
    left: 8px;
    z-index: 3;
    display: flex;
    gap: 6px;
}

.control-btn {
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.25);
    color: #fff;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 0.85rem;
    -webkit-tap-highlight-color: transparent;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
}
.control-btn:active {
    background: rgba(255, 255, 255, 0.3);
}

.chat-btn {
    position: absolute;
    bottom: 14px;
    right: 8px;
    z-index: 4;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    border: 1px solid rgba(99, 102, 241, 0.35);
    background: rgba(99, 102, 241, 0.2);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    color: #c7d2fe;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 0.85rem;
    -webkit-tap-highlight-color: transparent;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
}
.chat-btn:active {
    background: rgba(99, 102, 241, 0.35);
}

.chat-btn-unread {
    background: rgba(99, 102, 241, 0.4);
    border-color: rgba(129, 140, 248, 0.6);
    color: #fff;
    animation: pulse-unread 2s ease-in-out infinite;
}

.unread-dot {
    position: absolute;
    top: -2px;
    right: -2px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #f43f5e;
    border: 2px solid rgba(0, 0, 0, 0.3);
    box-shadow: 0 0 6px rgba(244, 63, 94, 0.6);
}

@keyframes pulse-unread {
    0%, 100% { box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35); }
    50% { box-shadow: 0 2px 12px rgba(99, 102, 241, 0.5); }
}

.call-now-btn {
    position: absolute;
    bottom: 14px;
    right: 8px;
    z-index: 4;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    border-radius: 20px;
    border: 1px solid rgba(72, 187, 120, 0.4);
    background: rgba(72, 187, 120, 0.2);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    color: #fff;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}
.call-now-btn:active {
    background: rgba(72, 187, 120, 0.35);
}

.call-now-online {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    color: #68d391;
}

.online-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #48bb78;
    box-shadow: 0 0 4px rgba(72, 187, 120, 0.8);
    animation: pulse-dot 1.5s ease-in-out infinite;
}

@keyframes pulse-dot {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
}

.call-now-action {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    color: #fff;
    border-left: 1px solid rgba(255, 255, 255, 0.2);
    padding-left: 8px;
}

.progress-track {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: rgba(255, 255, 255, 0.15);
    z-index: 3;
}
.progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #6366f1, #a78bfa);
    transition: width 0.25s linear;
}

.rate-row {
    display: flex;
    gap: 10px;
    padding: 10px 0 12px;
}

.rate-btn {
    flex: 1;
    padding: 22px 0;
    border: none;
    border-radius: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    -webkit-tap-highlight-color: transparent;
    transition: opacity 0.15s ease;
}
.rate-btn:disabled {
    opacity: 0.4;
    pointer-events: none;
}
.rate-btn:active {
    opacity: 0.7;
}

.reaction-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 5;
    pointer-events: none;
    font-size: 6rem;
}

.reaction-up {
    color: rgba(99, 102, 241, 0.85);
    filter: drop-shadow(0 4px 20px rgba(99, 102, 241, 0.4));
}

.reaction-down {
    color: rgba(148, 163, 184, 0.85);
    filter: drop-shadow(0 4px 20px rgba(148, 163, 184, 0.4));
}

.reaction-pop-enter-active {
    transition: all 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.reaction-pop-leave-active {
    transition: all 0.3s ease-out;
}
.reaction-pop-enter {
    opacity: 0;
    transform: scale(0.3);
}
.reaction-pop-leave-to {
    opacity: 0;
    transform: scale(1.2);
}

.rate-down {
    background: #f1f5f9;
    color: #94a3b8;
}

.rate-up {
    background: #6366f1;
    color: #fff;
}
</style>

<script>
import { mapState, mapGetters } from "vuex";

import CreatorVideoPlay from "@/components/creatorVideo/CreatorVideoPlay";
import CreatorVideoShareDialog from "@/components/creatorVideo/CreatorVideoShareDialog";
import UserOnlineStatus from "@/components/user/UserOnlineStatus";

export default {
    data() {
        return {
            currentIndex: 0,
            rating: false,
            reportModal: false,
            reported: false,
            reactionIcon: null,
            reactionClass: null,
            reactionKey: 0,
        };
    },
    watch: {
        currentVideo: {
            immediate: true,
            handler(video) {
                this.$store.commit(
                    "videoPlayer/setCurrentCreatorVideo",
                    video || null
                );
                this.$store.commit("videoIndex/resetViewTracking");
            },
        },
    },
    mounted() {
        if (this.$route.params?.slug) {
            this.$store.commit("videoIndex/setPage", 1);
            this.$store.commit("videoIndex/setSlug", this.$route.params?.slug);
        }

        this.refresh();
    },
    computed: {
        currentVideo() {
            if (!this.creatorVideos?.length) return null;
            return this.creatorVideos[this.currentIndex] || null;
        },
        isCreatorOnline() {
            if (!this.currentVideo?.standardMap?.id) return false;
            return this.isUserOnline(this.currentVideo.standardMap.id);
        },
        hasUnreadFromCreator() {
            if (!this.currentVideo?.standardMap?.id || !this.filteredMessages) return false;
            return this.filteredMessages.some(
                (m) => !m.self && !m.seen && m.userId === this.currentVideo.standardMap.id
            );
        },
        ...mapGetters("onlinePresence", ["isUserOnline"]),
        ...mapGetters("message", ["filteredMessages"]),
        ...mapState("videoIndex", [
            "creatorVideos",
            "reachedTheEnd",
            "isPlaying",
            "isMuted",
            "progress",
        ]),
    },
    methods: {
        togglePlay() {
            this.$store.commit("videoIndex/setIsPlaying", !this.isPlaying);
        },
        toggleMute() {
            this.$store.commit("videoIndex/setIsMuted", !this.isMuted);
        },
        async rate(value) {
            if (!this.currentVideo || this.rating) return;

            this.rating = true;

            this.reactionIcon = value === 1
                ? "fa-solid fa-thumbs-up"
                : "fa-solid fa-thumbs-down";
            this.reactionClass = value === 1
                ? "reaction-up"
                : "reaction-down";
            this.reactionKey++;

            this.$store.dispatch("videoIndex/rate", {
                creatorVideoId: this.currentVideo.creatorVideo.id,
                rating: value,
            });

            await new Promise((r) => setTimeout(r, 500));
            this.reactionIcon = null;

            await this.advance();
            this.rating = false;
        },
        async advance() {
            const nextIndex = this.currentIndex + 1;

            if (nextIndex < this.creatorVideos.length) {
                this.currentIndex = nextIndex;
            } else {
                await this.$store.dispatch("videoIndex/next");
                this.currentIndex = 0;
            }
        },
        async startOver() {
            this.currentIndex = 0;
            await this.$store.dispatch("videoIndex/startOver");
        },
        async refresh() {
            await this.$store.dispatch("videoIndex/refresh");
        },
        callCreator() {
            if (!this.currentVideo?.standardMap?.id) return;
            this.$store.dispatch("call/start", {
                userId: this.currentVideo.standardMap.id,
                standardMap: this.currentVideo.standardMap,
            });
        },
        chatCreator() {
            if (!this.currentVideo?.standardMap) return;
            this.$store.dispatch("profile/viewProfile", {
                userId: this.currentVideo.standardMap.id,
                profile: this.currentVideo.standardMap,
                view: "conversation",
            });
        },
        viewProfile(standardMap) {
            this.$store.dispatch("profile/viewProfile", {
                userId: standardMap.id,
                profile: standardMap,
            });
        },
        async reportVideo() {
            if (!this.currentVideo) return;
            this.api.post(
                `/creatorVideo/${this.currentVideo.creatorVideo.id}/report`
            );
            this.reported = true;
            await this.advance();
        },
    },
    components: {
        CreatorVideoPlay,
        CreatorVideoShareDialog,
        UserOnlineStatus,
    },
};
</script>
